import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  NewProjectState, 
  NewProjectActions, 
  NewProjectHookReturn,
  NewProjectFormData,
  NewProjectImage,
  NewSubProject,
  NewProjectValidationState,
  NewProjectUIState,
  PendingChanges
} from '../../types/newProject';
import { newProjectService } from '../../services/newProjectService.ts';
import { newValidationService } from '../../services/newValidationService.ts';
import { openMainModal, closeMainModal } from '../../store/actions/profileActions';
import { useNewProjectCreation } from './useNewProjectCreation.ts';


// Estado inicial
const initialProjectData: NewProjectFormData = {
  name: '',
  description: '',
  startDate: '',
  endDate: '',
  zone: '',
  image: ''
};

const initialUIState: NewProjectUIState = {
  currentStep: 1,
  isSubProjectModalOpen: false,
  editingSubProjectId: null,
  isImageModalOpen: false,
  isDirty: false,
  hasUnsavedChanges: false,
  isLoading: false,
  showPublishButton: false
};

const initialValidationState: NewProjectValidationState = {
  project: {},
  subProjects: {},
  global: {}
};

export const useNewProjectManager = (projectId?: string): NewProjectHookReturn => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { deleteProjectWithFlow } = useNewProjectCreation();

  const initialPendingChanges: PendingChanges = useMemo(() => ({
    project: {
      hasChanges: false,
      data: null,
      originalData: null
    },
    subProjects: {
      toCreate: [],
      toUpdate: [],
      toDelete: []
    }
  }), []);

  const [state, setState] = useState<NewProjectState>({
    projectData: initialProjectData,
    projectImages: [],
    subProjects: [],
    validation: initialValidationState,
    ui: initialUIState,
    mode: projectId ? 'edit' : 'create',
    projectId,
    pendingChanges: initialPendingChanges
  });

  // Redux state (reutilizar estado existente) - usando selectores específicos
  const userData = useSelector((state: any) => state.general.userData);
  const postulationProject = useSelector((state: any) => state.dashboard.postulationProject);
  const sendSubprojects = useSelector((state: any) => state.profile.sendSubprojects);
  const projectToEdit = useSelector((state: any) => state.profile.projectToEdit);
  
  // Combinar en un objeto memoizado
  const reduxState = useMemo(() => ({
    userData,
    postulationProject,
    sendSubprojects,
    projectToEdit
  }), [userData, postulationProject, sendSubprojects, projectToEdit]);

  // Inicializar estado si estamos editando
  useEffect(() => {
    if (state.mode === 'edit' && reduxState.postulationProject) {
      const project = reduxState.postulationProject;
      setState(prev => ({
        ...prev,
        projectData: {
          name: project.name || '',
          description: project.description || '',
          startDate: project.startDate || '',
          endDate: project.endDate || '',
          zone: project.zone || '',
          image: project.proyecto_images?.[0]?.image || ''
        },
        projectImages: project.proyecto_images?.map((img: any) => ({
          id: img.id?.toString(),
          file: null,
          preview: img.image,
          isNew: false,
          isUploaded: true
        })) || [],
        subProjects: project.subproyecto?.map((sub: any) => ({
          id: sub.id?.toString(),
          name: sub.name || '',
          description: sub.description || '',
          startDate: sub.startDate || '',
          endDate: sub.endDate || '',
          subProyectoListId: sub.subProyectoListId,
          link: sub.link || '',
          // Mapear archivos existentes
          existingPlanos: sub.planos ? { 
            name: sub.fileName || 'planos.pdf',
            url: sub.planos 
          } : null,
          existingComputoMetrico: sub.metric_computation ? { 
            name: 'computo_metrico.pdf',
            url: sub.metric_computation 
          } : null,
          existingEspecificacionesTecnicas: sub.technical_specifications ? { 
            name: 'especificaciones_tecnicas.pdf',
            url: sub.technical_specifications 
          } : null,
          existingPresupuesto: sub.budget_payroll ? { 
            name: 'presupuesto.pdf',
            url: sub.budget_payroll 
          } : null,
          isNew: false,
          isEdited: false
        })) || []
      }));
    }
  }, [state.mode, reduxState.postulationProject]);

  // Validar si el proyecto puede ser publicado
  const canPublish = useMemo(() => {
    const hasValidProject = newValidationService.validateProjectData(state.projectData);
    const hasSubProjects = state.subProjects.length > 0;
    const hasValidSubProjects = state.subProjects.every(sub => 
      newValidationService.validateSubProjectData(sub)
    );
    
    return hasValidProject && hasSubProjects && hasValidSubProjects;
  }, [state.projectData, state.subProjects]);

  // Validar si el formulario es válido
  const isValid = useMemo(() => {
    return newValidationService.validateProjectData(state.projectData) && 
           state.subProjects.length > 0;
  }, [state.projectData, state.subProjects]);

  // Detectar si hay cambios pendientes
  const hasPendingChanges = useMemo(() => {
    if (state.mode === 'create') return false;
    
    return state.pendingChanges?.project.hasChanges || 
           (state.pendingChanges?.subProjects.toCreate.length || 0) > 0 ||
           (state.pendingChanges?.subProjects.toUpdate.length || 0) > 0 ||
           (state.pendingChanges?.subProjects.toDelete.length || 0) > 0;
  }, [state.mode, state.pendingChanges]);

  // Detectar si estamos en modo edición
  const isEditingMode = useMemo(() => {
    return state.mode === 'edit' && !!state.projectId;
  }, [state.mode, state.projectId]);

  // Actualizar datos del proyecto
  const updateProjectData = useCallback((data: Partial<NewProjectFormData>) => {
    setState(prev => {
      const newProjectData = { ...prev.projectData, ...data };
      
      // Si estamos en modo edición, marcar como pendiente de guardar
      if (isEditingMode) {
        return {
          ...prev,
          projectData: newProjectData,
          ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true },
          pendingChanges: {
            ...prev.pendingChanges!,
            project: {
              hasChanges: true,
              data: newProjectData,
              originalData: prev.pendingChanges?.project.originalData || prev.projectData
            }
          }
        };
      }
      
      // Modo creación: comportamiento normal
      return {
        ...prev,
        projectData: newProjectData,
        ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true }
      };
    });
  }, [isEditingMode]);

  // Gestionar imágenes del proyecto
  const setProjectImages = useCallback((images: NewProjectImage[]) => {
    setState(prev => {
      if (isEditingMode) {
        // En modo edición: marcar como pendiente de guardar
        return {
          ...prev,
          projectImages: images,
          ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true },
          pendingChanges: {
            ...prev.pendingChanges!,
            project: {
              hasChanges: true,
              data: prev.projectData,
              originalData: prev.pendingChanges?.project.originalData || prev.projectData
            }
          }
        };
      }
      
      // Modo creación: comportamiento normal
      return {
        ...prev,
        projectImages: images,
        ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true }
      };
    });
  }, [isEditingMode]);

  const addProjectImage = useCallback((image: NewProjectImage) => {
    setState(prev => {
      const newImages = [...prev.projectImages, image];
      
      if (isEditingMode) {
        // En modo edición: marcar como pendiente de guardar
        return {
          ...prev,
          projectImages: newImages,
          ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true },
          pendingChanges: {
            ...prev.pendingChanges!,
            project: {
              hasChanges: true,
              data: prev.projectData,
              originalData: prev.pendingChanges?.project.originalData || prev.projectData
            }
          }
        };
      }
      
      // Modo creación: comportamiento normal
      return {
        ...prev,
        projectImages: newImages,
        ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true }
      };
    });
  }, [isEditingMode]);

  const removeProjectImage = useCallback((imageId: string) => {
    setState(prev => {
      const newImages = prev.projectImages.filter(img => img.id !== imageId);
      
      if (isEditingMode) {
        // En modo edición: marcar como pendiente de guardar
        return {
          ...prev,
          projectImages: newImages,
          ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true },
          pendingChanges: {
            ...prev.pendingChanges!,
            project: {
              hasChanges: true,
              data: prev.projectData,
              originalData: prev.pendingChanges?.project.originalData || prev.projectData
            }
          }
        };
      }
      
      // Modo creación: comportamiento normal
      return {
        ...prev,
        projectImages: newImages,
        ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true }
      };
    });
  }, [isEditingMode]);

  const addSubProject = useCallback((subProject: NewSubProject): string => {
    const newId = subProject.id || `temp_${Date.now()}`;
    const newSubProject: NewSubProject = {
      ...subProject,
      id: newId,
      isNew: true
    };

    setState(prev => {
      if (isEditingMode) {
        return {
          ...prev,
          subProjects: [...prev.subProjects, newSubProject],
          ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true },
          pendingChanges: {
            ...prev.pendingChanges!,
            subProjects: {
              ...prev.pendingChanges!.subProjects,
              toCreate: [...prev.pendingChanges!.subProjects.toCreate, newSubProject]
            }
          }
        };
      }
      return {
        ...prev,
        subProjects: [...prev.subProjects, newSubProject],
        ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true }
      };
    });
    return newId;
  }, [isEditingMode]);

  const updateSubProject = useCallback((subProjectId: string, data: Partial<NewSubProject>) => {
    setState(prev => {
      const updatedSubProjects = prev.subProjects.map(sub => 
        sub.id === subProjectId 
          ? { ...sub, ...data, isEdited: true }
          : sub
      );
      
      if (isEditingMode) {
        // En modo edición: agregar a cambios pendientes
        const updatedSubProject = updatedSubProjects.find(sub => sub.id === subProjectId);
        const existingUpdateIndex = prev.pendingChanges?.subProjects.toUpdate.findIndex(s => s.id === subProjectId) ?? -1;
        
        let newToUpdate = [...(prev.pendingChanges?.subProjects.toUpdate || [])];
        if (existingUpdateIndex >= 0) {
          newToUpdate[existingUpdateIndex] = updatedSubProject!;
        } else {
          newToUpdate.push(updatedSubProject!);
        }
        
        return {
          ...prev,
          subProjects: updatedSubProjects,
          ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true },
          pendingChanges: {
            ...prev.pendingChanges!,
            subProjects: {
              ...prev.pendingChanges!.subProjects,
              toUpdate: newToUpdate
            }
          }
        };
      }
      
      // Modo creación: comportamiento normal
      return {
        ...prev,
        subProjects: updatedSubProjects,
        ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true }
      };
    });
  }, [isEditingMode]);

  const removeSubProject = useCallback((subProjectId: string) => {
    setState(prev => {
      if (isEditingMode) {
        // En modo edición: agregar a cambios pendientes
        const isNewSubProject = prev.pendingChanges?.subProjects.toCreate.some(s => s.id === subProjectId);
        
        if (isNewSubProject) {
          // Si es un subproyecto nuevo, solo removerlo de la lista
          return {
            ...prev,
            subProjects: prev.subProjects.filter(sub => sub.id !== subProjectId),
            ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true },
            pendingChanges: {
              ...prev.pendingChanges!,
              subProjects: {
                ...prev.pendingChanges!.subProjects,
                toCreate: prev.pendingChanges!.subProjects.toCreate.filter(s => s.id !== subProjectId)
              }
            }
          };
        } else {
          // Si es un subproyecto existente, agregarlo a la lista de eliminación
          return {
            ...prev,
            subProjects: prev.subProjects.filter(sub => sub.id !== subProjectId),
            ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true },
            pendingChanges: {
              ...prev.pendingChanges!,
              subProjects: {
                ...prev.pendingChanges!.subProjects,
                toDelete: [...prev.pendingChanges!.subProjects.toDelete, subProjectId]
              }
            }
          };
        }
      }
      
      // Modo creación: comportamiento normal
      return {
        ...prev,
        subProjects: prev.subProjects.filter(sub => sub.id !== subProjectId),
        ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true }
      };
    });
  }, [isEditingMode]);

  const reorderSubProjects = useCallback((fromIndex: number, toIndex: number) => {
    setState(prev => {
      const newSubProjects = [...prev.subProjects];
      const [movedItem] = newSubProjects.splice(fromIndex, 1);
      newSubProjects.splice(toIndex, 0, movedItem);
      
      return {
        ...prev,
        subProjects: newSubProjects,
        ui: { ...prev.ui, isDirty: true, hasUnsavedChanges: true }
      };
    });
  }, []);

  // Gestionar validación
  const setValidation = useCallback((validation: Partial<NewProjectValidationState>) => {
    setState(prev => ({
      ...prev,
      validation: { ...prev.validation, ...validation }
    }));
  }, []);

  const clearValidation = useCallback(() => {
    setState(prev => ({
      ...prev,
      validation: initialValidationState
    }));
  }, []);

  // Gestionar UI
  const setUIState = useCallback((uiState: Partial<NewProjectUIState>) => {
    setState(prev => ({
      ...prev,
      ui: { ...prev.ui, ...uiState }
    }));
  }, []);

  const setCurrentStep = useCallback((step: number) => {
    setState(prev => ({
      ...prev,
      ui: { ...prev.ui, currentStep: step }
    }));
  }, []);

  const openSubProjectModal = useCallback((subProjectId?: string) => {
    setState(prev => ({
      ...prev,
      ui: { 
        ...prev.ui, 
        isSubProjectModalOpen: true,
        editingSubProjectId: subProjectId || null
      }
    }));
  }, []);

  const closeSubProjectModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      ui: { 
        ...prev.ui, 
        isSubProjectModalOpen: false,
        editingSubProjectId: null
      }
    }));
  }, []);

  // Validar proyecto completo
  const validateProject = useCallback((): boolean => {
    const projectValidation = newValidationService.validateProjectData(state.projectData);
    const subProjectsValidation = state.subProjects.every(sub => 
      newValidationService.validateSubProjectData(sub)
    );
    
    const isValid = projectValidation && subProjectsValidation && state.subProjects.length > 0;
    
    if (!isValid) {
      const validation = newValidationService.getValidationErrors(state.projectData, state.subProjects);
      setValidation(validation);
    } else {
      clearValidation();
    }
    
    return isValid;
  }, [state.projectData, state.subProjects, setValidation, clearValidation]);

  // Guardar proyecto
  const saveProject = useCallback(async (): Promise<void> => {
    if (!validateProject()) {
      throw new Error('El proyecto no es válido');
    }

    setState(prev => ({ ...prev, ui: { ...prev.ui, isLoading: true } }));

    try {
      if (state.mode === 'create') {
        // Crear nuevo proyecto
        const project = await newProjectService.createProject(state.projectData, state.projectImages);
        
        // Crear subproyectos
        const subProjectPromises = state.subProjects.map(subProject => 
          newProjectService.createSubProject(subProject, project.id, state.projectData.startDate, state.projectData.endDate)
        );
        
        await Promise.all(subProjectPromises);
        
        // Navegar al proyecto creado
        navigate(`/obrasprivadas/${project.id}`);
      } else {
        // Actualizar proyecto existente
        if (state.projectId) {
          await newProjectService.updateProject(state.projectId, state.projectData);
          
          // Actualizar subproyectos
          for (const subProject of state.subProjects) {
            if (subProject.isNew) {
              await newProjectService.createSubProject(subProject, state.projectId, state.projectData.startDate, state.projectData.endDate);
            } else if (subProject.isEdited) {
              await newProjectService.updateSubProject(subProject.id!, subProject, state.projectData.startDate, state.projectData.endDate);
            }
          }
          
          // Navegar al proyecto actualizado
          navigate(`/obrasprivadas/${state.projectId}`);
        }
      }
      
      setState(prev => ({ 
        ...prev, 
        ui: { ...prev.ui, isLoading: false, hasUnsavedChanges: false }
      }));
    } catch (error) {
      setState(prev => ({ ...prev, ui: { ...prev.ui, isLoading: false } }));
      throw error;
    }
  }, [state, validateProject, navigate]);

  // Guardar todos los cambios pendientes (modo edición)
  const saveAllChanges = useCallback(async (): Promise<void> => {
    if (!isEditingMode || !state.projectId || !hasPendingChanges) {
      return;
    }

    // Validar que el proyecto tenga al menos 1 subproyecto después de los cambios
    const currentSubProjects = state.subProjects.filter(sub => !state.pendingChanges?.subProjects.toDelete.includes(sub.id!));
    const newSubProjects = state.pendingChanges?.subProjects.toCreate || [];
    const totalSubProjects = currentSubProjects.length + newSubProjects.length;

    if (totalSubProjects === 0) {
      // Mostrar modal de confirmación para eliminar el proyecto completo
      dispatch(openMainModal({
        title: '¿Eliminar proyecto completo?',
        body: 'El proyecto debe tener al menos 1 rubro para cotizar. ¿Deseas eliminar todo el proyecto o agregar un rubro?',
        iconType: 'warning',
        isSuccess: false,
        confirmHandler: () => {
          dispatch(closeMainModal());
          deleteProjectWithFlow(state.projectId!);
        },
        cancelHandler: () => {
          dispatch(closeMainModal());
        }
      }));
      return;
    }

    setState(prev => ({ ...prev, ui: { ...prev.ui, isLoading: true } }));

    try {
      // 1. Actualizar proyecto padre si hay cambios
      if (state.pendingChanges?.project.hasChanges && state.pendingChanges.project.data) {
        await newProjectService.updateProject(state.projectId, state.pendingChanges.project.data, state.projectImages);
      }
      
      // 2. Crear nuevos subproyectos
      if (state.pendingChanges?.subProjects.toCreate.length > 0) {
        const createPromises = state.pendingChanges.subProjects.toCreate.map(subProject => 
          newProjectService.createSubProject(subProject, state.projectId!, state.projectData.startDate, state.projectData.endDate)
        );
        await Promise.all(createPromises);
      }
      
      // 3. Actualizar subproyectos existentes
      if (state.pendingChanges?.subProjects.toUpdate.length > 0) {
        const updatePromises = state.pendingChanges.subProjects.toUpdate.map(subProject => 
          newProjectService.updateSubProject(subProject.id!, subProject, state.projectData.startDate, state.projectData.endDate)
        );
        await Promise.all(updatePromises);
      }
      
      // 4. Eliminar subproyectos (si los hay)
      if (state.pendingChanges?.subProjects.toDelete.length > 0) {
        const deletePromises = state.pendingChanges.subProjects.toDelete.map(subProjectId => 
          newProjectService.deleteSubProject(subProjectId)
        );
        await Promise.all(deletePromises);
      }
      
      // 5. Limpiar cambios pendientes
      setState(prev => ({
        ...prev,
        pendingChanges: initialPendingChanges,
        ui: { ...prev.ui, isLoading: false, hasUnsavedChanges: false }
      }));
      
      // 6. Navegar directamente al proyecto actualizado
      navigate(`/obrasprivadas/${state.projectId}`);
      
    } catch (error) {
      console.error('Error al guardar cambios:', error);
      setState(prev => ({ ...prev, ui: { ...prev.ui, isLoading: false } }));
      
      dispatch(openMainModal({
        title: 'Error',
        body: 'Hubo un error al guardar algunos cambios. Por favor, inténtalo de nuevo.',
        iconType: 'error',
        isSuccess: false,
        confirmHandler: () => {
          dispatch(closeMainModal());
        }
      }));
      
      throw error;
    }
  }, [isEditingMode, state.projectId, hasPendingChanges, state.pendingChanges, state.projectImages, state.projectData.startDate, state.projectData.endDate, state.subProjects, dispatch, navigate, initialPendingChanges, deleteProjectWithFlow]);

  // Resetear proyecto
  const resetProject = useCallback(() => {
    setState({
      projectData: initialProjectData,
      projectImages: [],
      subProjects: [],
      validation: initialValidationState,
      ui: initialUIState,
      mode: projectId ? 'edit' : 'create',
      projectId
    });
  }, [projectId]);

  // Actualizar botón de publicar
  useEffect(() => {
    setState(prev => ({
      ...prev,
      ui: { ...prev.ui, showPublishButton: canPublish }
    }));
  }, [canPublish]);

  const actions: NewProjectActions = {
    updateProjectData,
    setProjectImages,
    addProjectImage,
    removeProjectImage,
    addSubProject,
    updateSubProject,
    removeSubProject,
    reorderSubProjects,
    setValidation,
    clearValidation,
    setUIState,
    setCurrentStep,
    openSubProjectModal,
    closeSubProjectModal,
    saveProject,
    saveAllChanges,
    resetProject,
    validateProject
  };

  return {
    state,
    actions,
    isValid,
    canPublish,
    hasPendingChanges,
    isEditingMode
  };
};
